import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LayoutTemplate, Trash2, Plus, Sparkles, Type } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "sonner";

const BODY_MODULES = {
  toolbar: [["bold", "italic", "underline"], [{ list: "bullet" }, { list: "ordered" }], ["clean"]],
};
const BODY_FORMATS = ["bold", "italic", "underline", "list"];

const TONES = [
  { value: "chaleureux", label: "Chaleureux" },
  { value: "empathique", label: "Empathique" },
  { value: "professionnel", label: "Professionnel" },
  { value: "formel", label: "Formel" },
];
const LENGTHS = [
  { value: "court", label: "Court" },
  { value: "moyen", label: "Moyen" },
  { value: "long", label: "Long" },
];

const EMPTY = { name: "", type: "ia", instructions: "", tone: "chaleureux", length: "moyen", subject: "", body: "" };

export const EmailTemplatesSection = () => {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.name.trim()) {
      toast.error("Donnez un nom au modèle");
      return;
    }
    if (form.type === "texte" && !form.body.replace(/<[^>]+>/g, "").trim()) {
      toast.error("Rédigez le corps du modèle texte");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/templates", form);
      setTemplates((l) => [...l, res.data]);
      setForm(EMPTY);
      setShowForm(false);
      toast.success("Modèle enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      setTemplates((l) => l.filter((x) => x.id !== t.id));
      toast.success("Modèle supprimé");
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 mt-12">
        <div className="w-11 h-11 rounded-xl bg-[#C07761]/10 text-[#C07761] flex items-center justify-center">
          <LayoutTemplate className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="font-heading text-2xl font-semibold text-[#2D2C28]">Modèles d'e-mails</h2>
          <p className="text-[#757169] text-sm">
            Modèles « IA » (consignes de génération réutilisables) ou « Texte » (contenu fixe avec variables).
          </p>
        </div>
      </div>

      <div
        className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)] mt-6 space-y-4"
        data-testid="email-templates-section"
      >
        {templates.length === 0 && !showForm && (
          <p className="text-sm text-[#9c988f]">
            Aucun modèle pour l'instant. Créez-en un ici, ou depuis une séance via le bouton « Modèle ».
          </p>
        )}

        {templates.map((t) => (
          <div
            key={t.id}
            data-testid={`template-row-${t.id}`}
            className="flex items-center justify-between rounded-xl bg-[#FBF9F6] border border-[#EAE6DF] px-4 py-3"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              {t.type === "texte" ? (
                <Type className="w-4 h-4 text-[#B7854f] shrink-0" strokeWidth={1.5} />
              ) : (
                <Sparkles className="w-4 h-4 text-[#7B968B] shrink-0" strokeWidth={1.5} />
              )}
              <span className="text-sm text-[#2D2C28] truncate">{t.name}</span>
              <span className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0 ${
                t.type === "texte" ? "bg-[#D4A373]/20 text-[#B7854f]" : "bg-[#7B968B]/15 text-[#5e7a6e]"
              }`}>
                {t.type === "texte" ? "Texte" : "IA"}
              </span>
            </div>
            <button
              onClick={() => remove(t)}
              data-testid={`delete-template-${t.id}`}
              title="Supprimer ce modèle"
              className="p-2 rounded-lg text-[#C0BBB0] hover:text-[#B56B6B] hover:bg-[#B56B6B]/10 transition-colors shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}

        {!showForm ? (
          <Button
            variant="outline"
            onClick={() => setShowForm(true)}
            data-testid="new-template-btn"
            className="rounded-xl border-[#EAE6DF] text-[#575349]"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouveau modèle
          </Button>
        ) : (
          <div className="rounded-xl border border-[#EAE6DF] p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#757169]">Nom du modèle</Label>
                <Input
                  data-testid="template-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex. « Suivi exposition », « Rappel RDV »"
                  className="mt-1.5 bg-white rounded-xl"
                />
              </div>
              <div>
                <Label className="text-xs text-[#757169]">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="template-type-select" className="mt-1.5 bg-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ia">Modèle IA (guide la génération)</SelectItem>
                    <SelectItem value="texte">Modèle texte (contenu fixe)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.type === "ia" ? (
              <>
                <div>
                  <Label className="text-xs text-[#757169]">Instructions pour l'IA</Label>
                  <Textarea
                    data-testid="template-instructions-input"
                    value={form.instructions}
                    onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                    placeholder="Ex. rappeler le prochain rendez-vous, insister sur l'exercice de respiration…"
                    className="mt-1.5 bg-white rounded-xl min-h-[70px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#757169]">Ton</Label>
                    <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                      <SelectTrigger data-testid="template-tone-select" className="mt-1.5 bg-white rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#757169]">Longueur</Label>
                    <Select value={form.length} onValueChange={(v) => setForm({ ...form, length: v })}>
                      <SelectTrigger data-testid="template-length-select" className="mt-1.5 bg-white rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LENGTHS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-xs text-[#757169]">Objet de l'e-mail</Label>
                  <Input
                    data-testid="template-subject-input"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Ex. Suivi de notre séance du {date}"
                    className="mt-1.5 bg-white rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#757169]">Corps de l'e-mail</Label>
                  <div className="mt-1.5 email-editor signature-editor bg-white rounded-xl" data-testid="template-body-input">
                    <ReactQuill
                      theme="snow"
                      value={form.body}
                      onChange={(v) => setForm((f) => ({ ...f, body: v }))}
                      modules={BODY_MODULES}
                      formats={BODY_FORMATS}
                      placeholder={"Bonjour {prénom},\n\nMerci pour notre séance du {date}…"}
                    />
                  </div>
                  <p className="text-xs text-[#9c988f] mt-2">
                    Variables disponibles : <code>{"{prénom}"}</code>, <code>{"{nom}"}</code>,{" "}
                    <code>{"{date}"}</code>, <code>{"{thérapeute}"}</code> — remplacées automatiquement
                    lors de l'application sur une séance.
                  </p>
                </div>
              </>
            )}

            <div className="flex gap-3">
              <Button
                onClick={create}
                disabled={creating}
                data-testid="create-template-btn"
                className="flex-1 rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white"
              >
                {creating ? "Enregistrement…" : "Enregistrer le modèle"}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowForm(false); setForm(EMPTY); }}
                data-testid="cancel-template-btn"
                className="rounded-xl border-[#EAE6DF] text-[#575349]"
              >
                Annuler
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
